import { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { LockClosedIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Parse from '../../api/config';
import { useAuth } from '../AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
    Banner,
    Button,
    Card,
    CardHeader,
    EmptyState,
    LoadingOverlay,
    Modal,
    cx,
    roleLabel,
} from '../ui';

const formatDate = (value) => (value ? format(new Date(value), 'MMM d, yyyy') : '—');

// The machine names the server reports back, said the way a person would.
const EMAIL_KINDS = {
    approval_request: 'Approval needed',
    approval_rerequest: 'Request changed, approve again',
    denial: 'Request not approved',
    final_approval: 'Fully approved, with the PDF',
    cancellation: 'Request cancelled',
    invitation: 'Invitation to join',
};

const RoleChips = ({ roles, isSuperAdmin }) => {
    if (!roles.length) {
        return <span className="text-xs text-neutral-400">No roles</span>;
    }
    return (
        <div className="flex flex-wrap gap-1">
            {roles.map((role) => (
                <span
                    key={role}
                    className={cx(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xxs font-medium ring-1 ring-inset',
                        role === 'Administrator'
                            ? 'bg-brand-50 text-brand-800 ring-brand-200'
                            : 'bg-neutral-50 text-neutral-600 ring-neutral-200'
                    )}
                >
                    {role === 'Administrator' && isSuperAdmin && (
                        <LockClosedIcon className="h-2.5 w-2.5" aria-hidden="true" />
                    )}
                    {roleLabel(role)}
                </span>
            ))}
        </div>
    );
};

export const People = () => {
    const { startImpersonation, isImpersonating } = useAuth();
    const { isSuperAdmin } = usePermissions();
    const navigate = useNavigate();

    const viewAs = (member) => {
        startImpersonation({ name: member.name, email: member.email });
        navigate('/home/dashboard');
    };

    const [members, setMembers] = useState([]);
    const [assignableRoles, setAssignableRoles] = useState([]);
    const [allowedDomains, setAllowedDomains] = useState([]);
    const [superAdminEmail, setSuperAdminEmail] = useState('');

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [notice, setNotice] = useState(null);
    const [query, setQuery] = useState('');

    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRoles, setInviteRoles] = useState([]);

    const [testResult, setTestResult] = useState(null);

    const [editing, setEditing] = useState(null);
    const [editRoles, setEditRoles] = useState([]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const result = await Parse.Cloud.run('listMembers');
            setMembers(result.members || []);
            setAssignableRoles(result.assignableRoles || []);
            setAllowedDomains(result.allowedDomains || []);
            setSuperAdminEmail(result.superAdminEmail || '');
        } catch (error) {
            console.error('Could not load people:', error);
            setNotice({ tone: 'danger', text: error.message });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return members;
        return members.filter((m) => m.email.includes(q) || (m.name || '').toLowerCase().includes(q));
    }, [members, query]);

    const run = async (message, fn) => {
        setBusy(message);
        setNotice(null);
        try {
            const result = await fn();
            if (result?.message) setNotice({ tone: result.emailSent === false ? 'warning' : 'success', text: result.message });
            await refresh();
            return true;
        } catch (error) {
            console.error(error);
            setNotice({ tone: 'danger', text: error.message });
            return false;
        } finally {
            setBusy(null);
        }
    };

    const invite = async () => {
        const ok = await run('Sending invitation…', () =>
            Parse.Cloud.run('inviteMember', { email: inviteEmail.trim(), roles: inviteRoles })
        );
        if (ok) {
            setInviteOpen(false);
            setInviteEmail('');
            setInviteRoles([]);
        }
    };

    const sendTestEmails = async () => {
        setTestResult(null);
        try {
            setBusy('Sending test emails…');
            // The recipient is fixed on the server. There is nothing to pass.
            const result = await Parse.Cloud.run('sendTestEmails');
            setTestResult(result);
            setNotice(
                result.sent === result.total
                    ? { tone: 'success', text: `Sent ${result.sent} test emails to ${result.to}.` }
                    : { tone: 'warning', text: `Sent ${result.sent} of ${result.total} test emails.` }
            );
        } catch (error) {
            setNotice({ tone: 'danger', text: error.message });
        } finally {
            setBusy(null);
        }
    };

    const saveRoles = async () => {
        const ok = await run('Updating roles…', () =>
            Parse.Cloud.run('setMemberRoles', { email: editing.email, roles: editRoles })
        );
        if (ok) setEditing(null);
    };

    const remove = (member) => {
        const what =
            member.status === 'invited'
                ? `Revoke the invitation for ${member.email}?`
                : `Remove all access for ${member.email}? Their past requests are kept.`;
        if (!window.confirm(what)) return;

        return run('Removing access…', () =>
            member.status === 'invited'
                ? Parse.Cloud.run('revokeInvite', { email: member.email })
                : Parse.Cloud.run('removeMember', { email: member.email })
        );
    };

    const toggle = (list, setList, role) =>
        setList(list.includes(role) ? list.filter((r) => r !== role) : [...list, role]);

    const RolePicker = ({ selected, onToggle, lockAdministrator }) => (
        <fieldset>
            <legend className="text-sm font-medium text-neutral-800">Roles</legend>
            <p className="mt-0.5 text-xs text-neutral-500">
                Approval roles decide which requests land in this person's queue.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {assignableRoles.map((role) => {
                    const locked = lockAdministrator && role === 'Administrator';
                    return (
                        <label
                            key={role}
                            className={cx(
                                'flex items-center gap-2.5 rounded-md border p-2.5 text-sm',
                                locked
                                    ? 'cursor-not-allowed border-neutral-200 bg-neutral-50 text-neutral-500'
                                    : 'cursor-pointer border-neutral-200 hover:bg-neutral-50'
                            )}
                        >
                            <input
                                type="checkbox"
                                checked={selected.includes(role)}
                                onChange={() => !locked && onToggle(role)}
                                disabled={locked}
                            />
                            <span className="flex-1">{roleLabel(role)}</span>
                            {locked && <LockClosedIcon className="h-3.5 w-3.5 text-neutral-400" aria-hidden="true" />}
                        </label>
                    );
                })}
            </div>
        </fieldset>
    );

    return (
        <div className="space-y-4">
            {busy && <LoadingOverlay message={busy} />}

            {notice && <Banner tone={notice.tone}>{notice.text}</Banner>}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative sm:w-72">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search people"
                        className="!pl-9"
                        aria-label="Search people"
                    />
                </div>
                <Button variant="primary" onClick={() => setInviteOpen(true)}>
                    Invite someone
                </Button>
            </div>

            <Card className="overflow-hidden">
                {loading ? (
                    <EmptyState title="Loading people…" />
                ) : !visible.length ? (
                    <EmptyState
                        title={query ? 'Nobody matches' : 'No one has been invited yet'}
                        description={
                            query
                                ? 'Try a different search.'
                                : 'Invite a staff member and they can sign in with their Bethel Google account.'
                        }
                    />
                ) : (
                    <table className="min-w-full divide-y divide-neutral-200">
                        <thead className="bg-neutral-50">
                            <tr className="text-left text-xxs uppercase tracking-wider text-neutral-500">
                                <th scope="col" className="px-5 py-2.5 font-semibold">Person</th>
                                <th scope="col" className="px-5 py-2.5 font-semibold">Roles</th>
                                <th scope="col" className="hidden px-5 py-2.5 font-semibold sm:table-cell">Joined</th>
                                <th scope="col" className="px-5 py-2.5">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                            {visible.map((member) => (
                                <tr key={member.email} className="align-top hover:bg-neutral-50/70">
                                    <td className="px-5 py-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-neutral-900">
                                                {member.name || member.email}
                                            </span>
                                            {member.status === 'invited' && (
                                                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xxs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                                                    Invited
                                                </span>
                                            )}
                                            {member.isSuperAdmin && (
                                                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xxs font-medium text-brand-800 ring-1 ring-inset ring-brand-200">
                                                    Global administrator
                                                </span>
                                            )}
                                        </div>
                                        {member.name && <p className="mt-0.5 text-xs text-neutral-500">{member.email}</p>}
                                        {member.status === 'invited' && (
                                            <p className="mt-0.5 text-xs text-neutral-400">
                                                Hasn't signed in yet — roles apply when they do.
                                            </p>
                                        )}
                                    </td>

                                    <td className="px-5 py-4">
                                        <RoleChips roles={member.roles} isSuperAdmin={member.isSuperAdmin} />
                                    </td>

                                    <td className="hidden whitespace-nowrap px-5 py-4 text-sm text-neutral-500 sm:table-cell">
                                        {member.status === 'invited'
                                            ? `Invited ${formatDate(member.invitedAt)}`
                                            : formatDate(member.joinedAt)}
                                    </td>

                                    <td className="px-5 py-4">
                                        <div className="flex items-center justify-end gap-1.5">
                                            {isSuperAdmin && !isImpersonating && member.status !== 'invited' && !member.isSuperAdmin && (
                                                <Button size="sm" variant="ghost" onClick={() => viewAs(member)}>
                                                    View as
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => {
                                                    setEditing(member);
                                                    setEditRoles(member.roles);
                                                }}
                                            >
                                                Edit roles
                                            </Button>
                                            {!member.isSuperAdmin && (
                                                <Button size="sm" variant="danger" onClick={() => remove(member)}>
                                                    {member.status === 'invited' ? 'Revoke' : 'Remove'}
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <p className="text-xs text-neutral-500">
                Only {allowedDomains.map((d) => `@${d}`).join(' and ')} addresses can be invited or sign in.
                {superAdminEmail && ` ${superAdminEmail} is the permanent global administrator and cannot be changed.`}
            </p>

            <Card>
                <CardHeader
                    title="Email"
                    description="Send yourself one of every email the system sends, using sample data."
                    actions={
                        <Button size="sm" variant="secondary" onClick={sendTestEmails}>
                            Send test emails
                        </Button>
                    }
                />
                <div className="border-t border-neutral-100 px-5 py-4 text-sm text-neutral-600">
                    {testResult ? (
                        <>
                            <p className="text-neutral-800">
                                {testResult.sent} of {testResult.total} sent to {testResult.to}.
                            </p>
                            <ul className="mt-2 space-y-1">
                                {testResult.results.map((r) => (
                                    <li key={r.kind} className="flex items-baseline gap-2 text-xs">
                                        <span
                                            className={cx(
                                                'font-medium',
                                                r.sent ? 'text-emerald-700' : 'text-amber-700'
                                            )}
                                        >
                                            {r.sent ? 'Sent' : 'Not sent'}
                                        </span>
                                        <span className="text-neutral-500">
                                            {EMAIL_KINDS[r.kind] || r.kind}
                                            {r.error ? ` — ${r.error}` : ''}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            {!testResult.emailConfigured && (
                                <p className="mt-3 text-xs text-amber-700">
                                    Nothing was actually sent: the server has no mail transport configured
                                    yet, so it can't reach Google to send.
                                </p>
                            )}
                            {testResult.emailConfigured && !testResult.redirectedTo && (
                                <p className="mt-3 text-xs text-amber-700">
                                    Test mode is off — email now goes to real recipients.
                                </p>
                            )}
                        </>
                    ) : (
                        <p>
                            They go to {superAdminEmail || 'the global administrator'} and nowhere else. While
                            the system is in test mode, that's true of every email it sends — a real request
                            can't reach real approvers.
                        </p>
                    )}
                </div>
            </Card>

            {/* Invite */}
            <Modal
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                title="Invite someone"
                description="They'll get an email, and sign in with their Bethel Google account."
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setInviteOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={invite} disabled={!inviteEmail.trim()}>
                            Send invitation
                        </Button>
                    </>
                }
            >
                <div className="space-y-5">
                    <div>
                        <label htmlFor="inviteEmail" className="block text-sm font-medium text-neutral-800">
                            Bethel email address
                        </label>
                        <input
                            id="inviteEmail"
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder={`name@${allowedDomains[0] || 'bethelchristianschools.com'}`}
                            className="mt-1.5"
                        />
                        <p className="mt-1.5 text-xs text-neutral-500">
                            Must end in {allowedDomains.map((d) => `@${d}`).join(' or ')}.
                        </p>
                    </div>

                    <RolePicker
                        selected={inviteRoles}
                        onToggle={(role) => toggle(inviteRoles, setInviteRoles, role)}
                    />
                </div>
            </Modal>

            {/* Edit roles */}
            <Modal
                open={!!editing}
                onClose={() => setEditing(null)}
                title="Edit roles"
                description={editing?.email}
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setEditing(null)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={saveRoles}>
                            Save roles
                        </Button>
                    </>
                }
            >
                {editing?.isSuperAdmin && (
                    <Banner tone="info" className="mb-5">
                        {editing.email} is the global administrator. That role is fixed in the server's code and
                        can't be taken away here.
                    </Banner>
                )}
                <RolePicker
                    selected={editRoles}
                    onToggle={(role) => toggle(editRoles, setEditRoles, role)}
                    lockAdministrator={editing?.isSuperAdmin}
                />
            </Modal>
        </div>
    );
};
